import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import SuccessAlert from "../child/Alert/SuccessAlert";
import DengerAlert from "../child/Alert/DengerAlert";

import {
  handleUnauthorized,
  showConfirmationDialog,
} from "../../utils/apiHelper";
import { getModulePermissions } from "../../utils/permissionUtils";
import moment from "moment";

const NotificationListLayer = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10); // Dynamically adjustable items per page
  const [selectedItems, setSelectedItems] = useState([]); // Track selected items
  const [successMessage, setSuccessMessage] = useState(""); // Manage Success Message
  const [errorMessage, setErrorMessage] = useState(""); // Manage Success Message
  const [selectedExport, setSelectedExport] = useState("");
  const [selectedSendType, setSelectedSendType] = useState("");
  const [sortColumn, setSortColumn] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const authToken = localStorage.getItem("authToken");
  const [selectedStatus, setSelectedStatus] = useState("");

  const { shouldCheckPermissions, canAdd, canView, canEdit, canDelete } =
    getModulePermissions("notification");
  const navigate = useNavigate();

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage, searchQuery, itemsPerPage, sortOrder, sortColumn, selectedSendType, selectedStatus,
  ]);
  const fetchData = async (page) => {
    const API_ENDPOINT = `${process.env.REACT_APP_BASE_URL}/admin/getNotificationAdmin?page=${page}&limit=${itemsPerPage}&sort=${sortColumn}&order=${sortOrder}&search=${searchQuery} &sentType=${selectedSendType}&status=${selectedStatus}`;
    try {
      setLoading(true);
      const response = await fetch(API_ENDPOINT, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      });
      if (response.status === 401) {
        handleUnauthorized(navigate);
        return;
      }
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();

      setItems(data.notifications || []);
      setTotalItems(data.metadata.totalNotifications || 0);
      setTotalPages(data.metadata.totalPages || 1);
    } catch (error) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };
  const handlePageChange = (pageNumber) => {
    //alert(pageNumber);
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      //fetchData(pageNumber);
    }
  };
  const handleItemsPerPageChange = (e) => {
    //alert(e.target.value);
    setItemsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(1); // Reset to the first page on items per page change
  };
  // Handle sorting when column is clicked
  const handleSort = (column) => {
    setSortColumn(column);
    setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
  };
  // Automatic city notices ("Almost time ✨", "{City} is live 🎉", founder
  // 2026-11-02): sent once, by the server, to one city's members. They can be
  // viewed and duplicated, never edited, resent or deleted (the server
  // refuses those too), so they are left out of select-all.
  const selectableItems = items.filter((item) => !item.cityNotice);
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(selectableItems.map((item) => item._id));
    } else {
      setSelectedItems([]);
    }
  };

  const getPaginationRange = () => {
    let start = Math.max(1, currentPage - 2);
    let end = Math.min(totalPages, start + 4); // Ensure only 5 items are shown (start + 4)

    if (end - start < 4) {
      start = Math.max(1, end - 4);
    }

    return Array.from({ length: end - start + 1 }, (_, index) => start + index);
  };
  const handleSelectItem = (id) => {
    setSelectedItems((prevSelected) => {
      if (prevSelected.includes(id)) {
        return prevSelected.filter((item) => item !== id);
      } else {
        return [...prevSelected, id];
      }
    });
  };
  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };
  const isSelected = (id) => selectedItems.includes(id);
  const handleDeleteAll = async (id, single = "No") => {
    const idsToDelete = single === "Yes" ? [id] : selectedItems;
    if (idsToDelete.length === 0) {
      return;
    }
    if (idsToDelete.length == 1) {
      single = "Yes";
    }
    const message = `This action will delete the notification!`;
    const confirmmessage = `Yes, delete it!`;
    const result = await showConfirmationDialog(message, confirmmessage);
    if (!result.isConfirmed) return;
    try {
      const API_ENDPOINT = `${process.env.REACT_APP_BASE_URL}/admin/deleteNotificationAdmin`;
      const response = await fetch(API_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({ id: idsToDelete, type: "sent" }), // Ensure this is only an array of IDs
      });
      if (response.status === 401) {
        handleUnauthorized(navigate);
        return;
      }
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `HTTP error! status: ${response.status}`);
      }
      const successmessage =
        single === "Yes"
          ? "Notification deleted successfully!"
          : "Notification deleted successfully!";
      setSuccessMessage(successmessage);
      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
      if (single === "No") {
        setSelectedItems([]);
      }
      fetchData(currentPage);
    } catch (error) {
      setErrorMessage(error.message);
      setTimeout(() => {
        setErrorMessage("");
      }, 3000);
    }
  };

  const handleResend = async (id) => {
    const confirm = await showConfirmationDialog(
      "Do you want to resend this notification?",
      "Yes, resend it!"
    );
    if (!confirm.isConfirmed) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/admin/resendNotificationAdmin/${id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.status === 401) {
        handleUnauthorized(navigate);
        return;
      }

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || "Failed to resend notification");
      }

      setSuccessMessage("Notification resent successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
      fetchData(currentPage)
    } catch (err) {
      setErrorMessage(err.message || "Something went wrong");
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  const handleDuplicate = async (id) => {
    const confirm = await showConfirmationDialog(
      "Do you want to duplicate this notification?",
      "Yes, duplicate it!"
    );
    if (!confirm.isConfirmed) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/admin/duplicateNotificationAdmin/${id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.status === 401) {
        handleUnauthorized(navigate);
        return;
      }

      if (!response.ok) throw new Error("Failed to duplicate notification");

      setSuccessMessage("Notification duplicated successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);

      // Refresh list
      fetchData(currentPage);
    } catch (err) {
      setErrorMessage(err.message || "Something went wrong");
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };


  return (
    <div className="card h-100 p-0 radius-12">
      {successMessage && (
        <SuccessAlert
          message={successMessage}
          onClose={() => setSuccessMessage("")}
        />
      )}
      {errorMessage && (
        <DengerAlert
          message={errorMessage}
          onClose={() => setErrorMessage("")}
        />
      )}
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <span className="text-md fw-medium text-secondary-light mb-0">
            Show
          </span>
          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={itemsPerPage}
            defaultValue="Select Number"
            onChange={handleItemsPerPageChange}
          >
            <option value="10">10 items per page</option>
            <option value="20">20 items per page</option>
            <option value="50">50 items per page</option>
            <option value="100">100 items per page</option>
          </select>

          <form className="navbar-search">
            <input
              type="text"
              className="bg-base h-40-px w-auto"
              name="search"
              placeholder="Search by Title"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyUp={(e) => {
                if (e.target.value === "") {
                  setCurrentPage(1);
                  //window.location.reload(); // Reload page only when search is cleared
                }
              }}
            />
            <Icon icon="ion:search-outline" className="icon" />
            {searchQuery && (
              <button
                onClick={() => {
                  setSearchQuery("");
                  setCurrentPage(1);
                  // Reset your data here if needed
                }}
                style={{
                  position: "absolute",
                  insetInlineEnd: "1.9375rem",
                  top: "40%",
                  transform: "translateY(-50%)",
                  fontSize: "1.125rem",
                  color: "var(--black)", // Corrected from `color: var(--black)` which was outside the object
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <Icon icon="ic:round-close" />
              </button>
            )}
          </form>

          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={selectedSendType}
            onChange={(e) => {
              setSelectedSendType(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Types</option>
            <option value="Immediate">Immediate</option>
            <option value="Schedule">Scheduled</option>

          </select>

          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={selectedStatus}
            onChange={(e) => {
              setSelectedStatus(e.target.value);
              setCurrentPage(1);
            }}
          >  <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="scheduled">Scheduled</option>
            <option value="published">Published</option>
            <option value="expired">Expired</option>
          </select>

          {(!shouldCheckPermissions || canDelete) && (
            <button
              className="btn btn-danger"
              onClick={handleDeleteAll}
              disabled={selectedItems.length === 0}
            >
              Delete All
            </button>
          )}
        </div>
        {(!shouldCheckPermissions || canAdd) && (
          <Link
            to="/notification/sent"
            className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
          >
            <Icon
              icon="ic:baseline-plus"
              className="icon text-xl line-height-1"
            />
            Send Notification
          </Link>
        )}
      </div>
      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                {(!shouldCheckPermissions || canDelete) && (
                  <th scope="col">
                    <div className="d-flex align-items-center gap-10">
                      <div className="form-check style-check d-flex align-items-center">
                        <input
                          className="form-check-input radius-4 border input-form-dark"
                          type="checkbox"
                          name="checkbox"
                          id="selectAll"
                          onChange={handleSelectAll}
                          checked={
                            selectedItems.length === selectableItems.length &&
                            selectableItems.length > 0
                          }
                        />
                      </div>
                    </div>
                  </th>
                )}

                <th
                  onClick={() => handleSort("title")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-0">
                    Title
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${sortColumn === "title" && sortOrder === "asc"
                          ? "active"
                          : ""
                          }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${sortColumn === "title" && sortOrder === "desc"
                          ? "active"
                          : ""
                          }`}
                      />
                    </span>
                  </div>
                </th>
                <th>Sent Type</th>
                <th>Status</th>
                <th
                  onClick={() => handleSort("createdAt")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-0">
                    Date{" "}
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${sortColumn === "createdAt" && sortOrder === "asc"
                          ? "active"
                          : ""
                          }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${sortColumn === "createdAt" && sortOrder === "desc"
                          ? "active"
                          : ""
                          }`}
                      />
                    </span>
                  </div>
                </th>

                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="text-center">
                    <span className="loader-15"></span>
                  </td>
                </tr>
              ) : items.length > 0 ? (
                items.map((item, index) => (
                  <tr key={item._id}>
                    {(!shouldCheckPermissions || canDelete) && (
                      <td>
                        <div className="d-flex align-items-center gap-10">
                          <div className="form-check style-check d-flex align-items-center">
                            {!item.cityNotice && (
                              <input
                                className="form-check-input radius-4 border border-neutral-400"
                                type="checkbox"
                                name="checkbox"
                                value={item._id}
                                checked={isSelected(item._id)}
                                onChange={() => handleSelectItem(item._id)}
                              />
                            )}
                          </div>
                        </div>
                      </td>
                    )}
                    <td>
                      {item.title || "N/A"}
                      {item.cityNotice && (
                        <div className="text-sm text-secondary-light mt-1">
                          Automatic · {item.cityNotice.pocketName || "one city"} members only
                        </div>
                      )}
                    </td>
                    <td>{item.sentType || "N/A"}</td>
                    <td>
                      {item.status
                        ? item.status.charAt(0).toUpperCase() + item.status.slice(1)
                        : "N/A"}
                    </td>
                    <td>{moment(item.createdAt).format("DD-MM-YYYY")}</td>

                    <td>
                      <div className="d-flex align-items-center gap-10">
                        {(!shouldCheckPermissions || canView) && (
                          <button
                            title="View"
                            onClick={() =>
                              navigate(`/notification/view/sent/${item._id}`)
                            }
                            className="bg-info-focus bg-hover-info-200 text-info-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon
                              icon="majesticons:eye-line"
                              className="icon text-xl"
                            />


                          </button>
                        )}
                        {/* Edit / Update */}
                        {(!shouldCheckPermissions || canEdit) && !item.cityNotice && (
                          <button
                            title="Edit"
                            onClick={() => navigate(`/notification/edit/${item._id}`)}
                            className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"

                          >
                            <Icon icon="lucide:edit" className="menu-icon" />

                          </button>
                        )}

                        {/* Resend */}
                        {(!shouldCheckPermissions || canEdit) && !item.cityNotice && (
                          <button
                            title="Resend"
                            // onClick={() => handleResend(item._id)}
                            onClick={() => {
                              if (!item.sentType || (item.targetUsers && item.targetUsers.length === 0)) {
                                setErrorMessage("Please update to proceed: send type not selected or target users missing.");
                                setTimeout(() => setErrorMessage(""), 3000);
                                return;
                              }

                              handleResend(item._id);
                            }}

                            className="bg-warning-focus bg-hover-warning-200 text-warning-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon icon="mdi:send-circle-outline" />
                          </button>
                        )}

                        {/* Duplicate */}
                        {(!shouldCheckPermissions || canEdit) && (
                          <button
                            title="Duplicate"
                            onClick={() => handleDuplicate(item._id)}
                            className="bg-info-100 bg-hover-info-200 text-info-700 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          >
                            <Icon icon="mdi:content-duplicate" />
                          </button>
                        )}
                        {(!shouldCheckPermissions || canDelete) && !item.cityNotice && (
                          <button
                            type="button"
                            title="Delete"
                            className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                            onClick={() => handleDeleteAll(item._id, "Yes")}
                          >
                            <Icon
                              icon="fluent:delete-24-regular"
                              className="menu-icon"
                            />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center">
                    No Data Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
          <span>
            Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
            {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{" "}
            entries
          </span>
          <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <Link
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                to="#"
                onClick={handlePrev}
              >
                <Icon icon="ep:d-arrow-left" />
              </Link>
            </li>
            {getPaginationRange().map((page) => {
              // Return JSX for pagination items
              return (
                <li key={page} className="page-item">
                  <Link
                    className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${currentPage === page
                      ? "bg-primary-600 text-white"
                      : "bg-neutral-200"
                      }`}
                    to="#"
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </Link>
                </li>
              );
            })}

            <li
              className={`page-item ${currentPage === totalPages ? "disabled" : ""
                }`}
            >
              <Link
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                to="#"
                onClick={handleNext}
              >
                <Icon icon="ep:d-arrow-right" />
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default NotificationListLayer;
